import { getRelationId } from '../../payload/payload.helpers'
import { PayloadNote } from '../../payload/payload-types.zod'
import z from 'zod'

export const NoteWithoutEmbeddingSchema = PayloadNote.omit({
  embedding: true,
  relationTo: true,
})

export const NoteResponseSchema = PayloadNote.omit({
  individual: true,
  user: true,
  embedding: true,
  relationTo: true,
  contentChecksum: true,
  // Read-only join, computed from the errand's own `notes` array — same reasoning as
  // DocumentResponseSchema omitting it. Breaks the Note↔Errand recursive type at the response
  // level; the errand's own `notes` field already carries the relationship.
  errand: true,
}).extend({
  relatedToThread: z.string().optional(),
  // Total number of timeline entries (dated + time-ambiguous) shown in the overview.
  eventCount: z.number(),
})

// Dated timeline event (year required) — same element shape used by the shared timeline.
export const NoteActivitySchema = NoteResponseSchema.shape.activities.element
// Time-ambiguous event the AI could not resolve to a date.
export const NotePendingEventSchema = NoteResponseSchema.shape.pendingEvents.element

export const NoteListResponseSchema = NoteResponseSchema.omit({}).array()

export const NoteCreateRequestSchema = z.object({
  title: z.string().optional(),
  note: z.string().min(1),
  // Attaches the note to an errand the same way an errand-scoped document upload does — set only
  // when the note was created from the errand page's "Lägg till anteckning" button.
  errandId: z.string().optional(),
})

// Manual title edit. Setting the title this way locks it from future AI regeneration.
export const NoteTitleUpdateRequestSchema = z.object({
  title: z.string().min(1),
})

// Partial patch of any note event, dated (activity) or undated (pendingEvent),
// addressed by its id. The server merges these over the existing event and then
// re-buckets it: an event with a year becomes a dated activity (a full ISO date is
// derived only when year+month+day are all present); without a year it stays/returns
// to pendingEvents (which requires a timeText phrase). Every field is optional —
// omitted fields are left unchanged.
//
// This subsumes the old narrow operations:
//   confirm a pendingEvent  → PATCH { year, month?, day? }
//   adjust an activity date → PATCH { year, month?, day? }
//   edit text/type          → PATCH { description?, type?, timeText? }
export const NoteEventUpdateRequestSchema = z.object({
  year: z.number().int().min(1900).max(2100).optional(),
  month: z.number().int().min(1).max(12).optional(),
  day: z.number().int().min(1).max(31).optional(),
  description: z.string().min(3).max(500).optional(),
  type: NoteActivitySchema.shape.type,
  timeText: z.string().min(1).max(200).optional(),
})

export type PayloadNote = z.infer<typeof PayloadNote>
export type NoteResponse = z.infer<typeof NoteResponseSchema>
export type NoteListResponse = z.infer<typeof NoteListResponseSchema>
export type NoteCreateRequest = z.infer<typeof NoteCreateRequestSchema>
export type NoteTitleUpdateRequest = z.infer<typeof NoteTitleUpdateRequestSchema>
export type NoteEventUpdateRequest = z.infer<typeof NoteEventUpdateRequestSchema>
export type NoteActivity = z.infer<typeof NoteActivitySchema>
export type NotePendingEvent = z.infer<typeof NotePendingEventSchema>

export const noteToResponse = (data: Omit<PayloadNote, 'embedding'>): NoteResponse => {
  const relatedToThread = data.relationTo ? getRelationId(data.relationTo) : undefined
  const activities = data.activities ?? []
  const pendingEvents = data.pendingEvents ?? []

  return NoteResponseSchema.parse({
    id: data.id,
    title: data.title,
    note: data.note,
    titleIsManual: data.titleIsManual,
    generationStatus: data.generationStatus,
    activities,
    pendingEvents,
    eventCount: activities.length + pendingEvents.length,
    createdAt: data.createdAt,
    updatedAt: data.updatedAt,
    relatedToThread,
  } satisfies NoteResponse)
}

export const noteListToResponse = (data: Omit<PayloadNote, 'embedding'>[]): NoteListResponse => {
  return data.map(noteToResponse)
}
